import { useState, useEffect, createContext, useContext } from "react";
import { Settings, Lock } from "lucide-react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAsyncData } from "./hooks/useAsyncData";
import { loadMonthlyBillingData, loadAdminConfig, getDefaultAdminConfig, type AdminConfig } from "./lib/api";
import { BillingView } from "./components/BillingView";
import { AdminView } from "./components/AdminView";
import { LoadingScreen, ErrorScreen } from "./components/ui";
import type { BillingProcess, MonthlyBillingReport } from "./types";
import "./index.css";

// Keys for localStorage
const STORAGE_KEYS = {
  viewerAuth: "spt-billing-viewer-auth",
  adminAuth: "spt-billing-admin-auth",
  savedViewerKey: "spt-billing-saved-viewer-key",
  savedAdminKey: "spt-billing-saved-admin-key",
} as const;

// Context for sharing admin config between routes
const AdminConfigContext = createContext<{
  adminConfig: AdminConfig;
  setAdminConfig: React.Dispatch<React.SetStateAction<AdminConfig>>;
} | null>(null);

function AdminConfigProvider({ children }: { children: React.ReactNode }) {
  const [adminConfig, setAdminConfig] = useState<AdminConfig>(() => {
    const stored = loadAdminConfig();
    return stored || getDefaultAdminConfig();
  });

  // Persist admin config
  useEffect(() => {
    localStorage.setItem("spt-billing-admin-config", JSON.stringify(adminConfig));
  }, [adminConfig]);

  return (
    <AdminConfigContext.Provider value={{ adminConfig, setAdminConfig }}>
      {children}
    </AdminConfigContext.Provider>
  );
}

function useAdminConfig(): { adminConfig: AdminConfig; setAdminConfig: React.Dispatch<React.SetStateAction<AdminConfig>> } {
  const ctx = useContext(AdminConfigContext);
  if (!ctx) throw new Error("useAdminConfig must be used within AdminConfigProvider");
  return ctx;
}

function App() {
  return (
    <BrowserRouter>
      <AdminConfigProvider>
        <Routes>
          <Route path="/" element={<ViewerRoute />} />
          <Route path="/admin" element={<AdminRoute />} />
          <Route path="/admin/*" element={<AdminRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AdminConfigProvider>
    </BrowserRouter>
  );
}

function ViewerRoute() {
  const [viewerAuth, setViewerAuth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.viewerAuth);
    return stored === "true";
  });
  const [savedKey] = useState(() => localStorage.getItem(STORAGE_KEYS.savedViewerKey) || "");
  const [showLogin, setShowLogin] = useState(!viewerAuth);
  const [error, setError] = useState("");
  const [password, setPassword] = useState(savedKey);
  // Store the viewer password after successful login
  const [viewerPassword, setViewerPassword] = useState(savedKey);
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
    try {
      // Try to load data with the provided password - let the server validate
      await loadMonthlyBillingData(new AbortController().signal, new Date().getFullYear(), new Date().getMonth() + 1, "viewer", password);
      // If successful, password was correct
      setViewerAuth(true);
      localStorage.setItem(STORAGE_KEYS.viewerAuth, "true");
      localStorage.setItem(STORAGE_KEYS.savedViewerKey, password);
      setViewerPassword(password);
      setShowLogin(false);
    } catch (err) {
      // Server validation failed
      const errorMsg = err instanceof Error ? err.message : "Une erreur s'est produite";
      setError(errorMsg);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setViewerAuth(false);
    localStorage.removeItem(STORAGE_KEYS.viewerAuth);
    setShowLogin(true);
  };

  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  const deps: unknown[] = [selectedMonth.year, selectedMonth.month, viewerAuth, viewerPassword];
  const { data: report, loading, error: loadError } = useAsyncData<MonthlyBillingReport>(
    deps,
    ((_signal: AbortSignal, year: number, month: number, _auth: unknown, pwd: string) => loadMonthlyBillingData(_signal, year, month, "viewer", pwd)) as any,
    viewerAuth
  );

  if (showLogin) {
    return (
      <AuthScreen title="SPT Billing Analytics">
        <form onSubmit={handleLogin}>
          {error && <div className="auth-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="viewer-password">Mot de passe consultation</label>
            <input
              id="viewer-password"
              type="password"
              value={password}
              onChange={(e) => {
                const val = e.target.value;
                setPassword(val);
              }}
              placeholder="Entrez le mot de passe"
              autoFocus
              disabled={loginLoading}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loginLoading}>
            <Lock size={18} /> {loginLoading ? "Vérification..." : "Consulter les statistiques"}
          </button>
        </form>
      </AuthScreen>
    );
  }

  if (loading) return <LoadingScreen label="Chargement des données de facturation..." />;
  if (loadError || !report) return <ErrorScreen message={loadError || "Impossible de charger le rapport de facturation."} />;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1>Facturation SPT - Trak Platform</h1>
          <span className="subtitle">Analytiques &amp; Facturation Mensuelle</span>
        </div>
        <div className="header-right">
          <button className="btn btn-ghost" onClick={handleLogout}>
            <Lock size={18} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="app-main">
        <BillingView
          report={report!}
          isAdmin={false}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
        />
      </main>
    </div>
  );
}

function AdminRoute() {
  const { adminConfig, setAdminConfig } = useAdminConfig();
  const [adminAuth, setAdminAuth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.adminAuth);
    return stored === "true";
  });
  const [savedKey] = useState(() => localStorage.getItem(STORAGE_KEYS.savedAdminKey) || "");
  const [showLogin, setShowLogin] = useState(!adminAuth);
  const [error, setError] = useState("");
  const [password, setPassword] = useState(savedKey);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  // Store the admin password after successful login
  const [adminPassword, setAdminPassword] = useState(savedKey);
  const [loginLoading, setLoginLoading] = useState(false);
  const location = useLocation();

  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number }>(() => {
    const params = new URLSearchParams(location.search);
    const year = Number(params.get("year"));
    const month = Number(params.get("month"));
    if (year && month) return { year, month };
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("year", String(selectedMonth.year));
    params.set("month", String(selectedMonth.month));
    window.history.replaceState({}, "", `?${params.toString()}`);
  }, [selectedMonth, location]);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
    try {
      // Try to load data with the provided password - let the server validate
      await loadMonthlyBillingData(new AbortController().signal, new Date().getFullYear(), new Date().getMonth() + 1, "admin", password);
      // If successful, password was correct
      setAdminAuth(true);
      localStorage.setItem(STORAGE_KEYS.adminAuth, "true");
      localStorage.setItem(STORAGE_KEYS.savedAdminKey, password);
      setAdminPassword(password);
      setShowLogin(false);
    } catch (err) {
      // Server validation failed
      const errorMsg = err instanceof Error ? err.message : "Une erreur s'est produite";
      setError(errorMsg);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setAdminAuth(false);
    localStorage.removeItem(STORAGE_KEYS.adminAuth);
    setShowLogin(true);
    setShowAdminPanel(false);
  };

  if (showLogin) {
    return (
      <AuthScreen title="SPT Billing Analytics - Administration">
        <form onSubmit={handleAdminLogin}>
          {error && <div className="auth-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="admin-password">Mot de passe administrateur</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => {
                const val = e.target.value;
                setPassword(val);
              }}
              placeholder="Entrez le mot de passe"
              autoFocus
              disabled={loginLoading}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loginLoading}>
            <Lock size={18} /> {loginLoading ? "Vérification..." : "Accéder à l'administration"}
          </button>
        </form>
      </AuthScreen>
    );
  }

  if (showAdminPanel) {
    return (
      <AdminView
        config={adminConfig}
        onSave={setAdminConfig}
        onClose={() => setShowAdminPanel(false)}
      />
    );
  }

  const depsAdmin: unknown[] = [selectedMonth.year, selectedMonth.month, adminAuth, adminPassword];
  const { data: report, loading, error: loadError } = useAsyncData<MonthlyBillingReport>(
    depsAdmin,
    ((_signal: AbortSignal, year: number, month: number, _auth: unknown, pwd: string) => loadMonthlyBillingData(_signal, year, month, "admin", pwd)) as any,
    adminAuth
  );

  if (loading) return <LoadingScreen label="Chargement des données de facturation..." />;
  if (loadError || !report) return <ErrorScreen message={loadError || "Impossible de charger le rapport de facturation."} />;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1>Facturation SPT - Trak Platform</h1>
          <span className="subtitle">Analytiques &amp; Facturation Mensuelle</span>
        </div>
        <div className="header-right">
          <button className="btn btn-secondary" onClick={() => setShowAdminPanel(true)}>
            <Settings size={18} /> Administration
          </button>
          <button className="btn btn-ghost" onClick={handleLogout}>
            <Lock size={18} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="app-main">
        <BillingView
          report={report!}
          isAdmin={true}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          onEditProcess={(process: BillingProcess) => {
            setAdminConfig((prev: AdminConfig) => ({
              ...prev,
              processes: prev.processes.map((p: BillingProcess) =>
                p.processId === process.processId ? { ...p, ...process } : p
              ),
            }));
            setShowAdminPanel(true);
          }}
        />
      </main>
    </div>
  );
}

function AuthScreen({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="shell center-screen">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{title}</h1>
        </div>
        <div className="auth-content">{children}</div>
      </div>
    </div>
  );
}

export default App;